import { useState, useEffect, useRef } from "react";
import {
  Globe, Loader2, X, Check, Trash2, Plus, AlertTriangle, CalendarClock, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STAGES = ["Analysing homepage", "Reading services & pages", "Extracting business details & prices", "Building AI knowledge base", "Preparing your receptionist"];

export default function WebsiteImport({ onClose, onApplied }) {
  const [phase, setPhase] = useState("url"); // url | progress | review
  const [url, setUrl] = useState("");
  const [job, setJob] = useState(null);
  const [data, setData] = useState(null);
  const [saveStaff, setSaveStaff] = useState(true);
  const [saving, setSaving] = useState(false);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const start = async () => {
    if (!url.trim()) return toast.error("Please enter your website URL");
    setPhase("progress");
    try {
      const r = await api.post("/import/website", { url: url.trim() });
      poll.current = setInterval(async () => {
        try {
          const s = await api.get(`/import/website/${r.data.job_id}`);
          setJob(s.data);
          if (s.data.status === "done") { clearInterval(poll.current); setData(normalise(s.data.result)); setPhase("review"); }
          else if (s.data.status === "error") { clearInterval(poll.current); toast.error(s.data.error || "Import failed"); setPhase("url"); }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start import"); setPhase("url"); }
  };

  const normalise = (r) => ({
    business: r.business || {}, opening_hours: r.opening_hours || {}, policies: r.policies || {},
    services: r.services || [], staff: r.staff || [], faqs: r.faqs || [],
    logo: r.logo, social_links: r.social_links || {}, booking_detected: r.booking_detected || [],
    source_url: r.source_url, pages_scanned: r.pages_scanned,
  });

  const apply = async () => {
    setSaving(true);
    try {
      const res = await api.post("/import/website/apply", { ...data, save_staff: saveStaff });
      toast.success(`Imported ${res.data.services} services, ${res.data.faqs} FAQs${res.data.staff_added ? `, ${res.data.staff_added} staff` : ""}`);
      onApplied?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };

  const upd = (path, val) => setData((d) => { const c = structuredClone(d); let o = c; const ks = path.split("."); ks.slice(0, -1).forEach((k) => (o = o[k])); o[ks.at(-1)] = val; return c; });
  const updService = (i, k, v) => setData((d) => { const c = structuredClone(d); c.services[i][k] = v; return c; });
  const delService = (i) => setData((d) => { const c = structuredClone(d); c.services.splice(i, 1); return c; });
  const addService = () => setData((d) => ({ ...d, services: [...d.services, { name: "", price: "", duration: null, category: "", description: "" }] }));

  const progressPct = job?.progress || 5;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="website-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" /><h3 className="font-heading font-bold tracking-tight">Import from website</h3></div>
          <button data-testid="import-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        {phase === "url" && (
          <div className="p-8 text-center">
            <div className="h-14 w-14 rounded-full bg-blue-600 flex items-center justify-center text-white mx-auto"><Globe className="h-7 w-7" /></div>
            <h4 className="mt-4 font-heading text-xl font-bold">Enter your business website</h4>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">We'll analyse your site and auto-configure your AI receptionist — services, prices, hours, FAQs and more. Nothing is saved until you approve it.</p>
            <div className="mt-5 flex gap-2 max-w-md mx-auto">
              <Input data-testid="import-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.mybusiness.co.uk" onKeyDown={(e) => e.key === "Enter" && start()} />
              <Button data-testid="import-start-btn" onClick={start} className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0"><Sparkles className="h-4 w-4" /> Import My Business</Button>
            </div>
          </div>
        )}

        {phase === "progress" && (
          <div className="p-10" data-testid="import-progress">
            <div className="max-w-sm mx-auto">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${progressPct}%` }} /></div>
              <p className="mt-3 text-center text-sm font-semibold text-slate-700">{job?.stage || "Starting…"} <span className="text-slate-400">{progressPct}%</span></p>
              <ul className="mt-6 space-y-2">
                {STAGES.map((s) => {
                  const done = STAGES.indexOf(s) < STAGES.indexOf(job?.stage) || progressPct >= 100;
                  const active = s === job?.stage;
                  return (
                    <li key={s} className={`flex items-center gap-2 text-sm ${done ? "text-emerald-600" : active ? "text-blue-600 font-semibold" : "text-slate-400"}`}>
                      {done ? <Check className="h-4 w-4" /> : active ? <Loader2 className="h-4 w-4 animate-spin" /> : <div className="h-4 w-4 rounded-full border border-slate-300" />}
                      {s}…
                    </li>
                  );
                })}
              </ul>
              <p className="mt-6 text-center text-xs text-slate-400">This usually takes under two minutes.</p>
            </div>
          </div>
        )}

        {phase === "review" && data && (
          <div className="overflow-y-auto p-6 space-y-6" data-testid="import-review">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <Check className="h-4 w-4" /> Analysed {data.pages_scanned} page(s). Review & edit below — nothing is saved until you click Save.
            </div>

            {data.booking_detected?.length > 0 && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3" data-testid="booking-detected">
                <p className="text-sm font-semibold text-blue-900 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> We detected your booking system: {data.booking_detected.map((b) => b.label).join(", ")}</p>
                <p className="text-xs text-blue-700 mt-1">You can connect it after setup from Booking Systems{data.booking_detected.some((b) => b.connectable) ? "" : " (support coming soon)"}.</p>
              </div>
            )}

            <Section title="Business Information">
              <Field label="Business name" value={data.business.name} onChange={(v) => upd("business.name", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" value={data.business.phone} onChange={(v) => upd("business.phone", v)} />
                <Field label="Email" value={data.business.email} onChange={(v) => upd("business.email", v)} />
              </div>
              <Field label="Address" value={data.business.address} onChange={(v) => upd("business.address", v)} />
              <Field label="Business type" value={data.business.business_type} onChange={(v) => upd("business.business_type", v)} />
              <TextField label="Description" value={data.business.description} onChange={(v) => upd("business.description", v)} />
            </Section>

            <Section title={`Services (${data.services.length})`} action={<Button size="sm" variant="outline" onClick={addService} className="h-7 text-xs gap-1"><Plus className="h-3 w-3" /> Add</Button>}>
              {data.services.length === 0 && <p className="text-sm text-slate-400">No services detected — add them manually or fill in later.</p>}
              {data.services.map((s, i) => (
                <div key={i} data-testid={`import-service-${i}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input value={s.name} onChange={(e) => updService(i, "name", e.target.value)} placeholder="Service name" className="flex-1 h-8 text-sm" />
                    <button onClick={() => delService(i)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <PriceField label="Price" value={s.price} onChange={(v) => updService(i, "price", v)} />
                    <PriceField label="Duration (min)" value={s.duration || ""} onChange={(v) => updService(i, "duration", v)} />
                    <PriceField label="Category" value={s.category} onChange={(v) => updService(i, "category", v)} />
                  </div>
                </div>
              ))}
            </Section>

            {data.staff?.length > 0 && (
              <Section title={`Staff (${data.staff.length})`} action={<label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={saveStaff} onChange={(e) => setSaveStaff(e.target.checked)} data-testid="import-savestaff" /> Import staff</label>}>
                {data.staff.map((s, i) => <p key={i} className="text-sm text-slate-700">• <b>{s.name}</b>{s.title ? ` — ${s.title}` : ""}</p>)}
              </Section>
            )}

            <Section title="Opening Hours">
              <Field label="Normal hours" value={data.opening_hours.normal} onChange={(v) => upd("opening_hours.normal", v)} />
              <Field label="Holiday hours" value={data.opening_hours.holiday} onChange={(v) => upd("opening_hours.holiday", v)} />
            </Section>

            {data.faqs?.length > 0 && <Section title={`FAQs (${data.faqs.length})`}>{data.faqs.map((f, i) => <p key={i} className="text-sm text-slate-600">• {f.question}</p>)}</Section>}

            <Section title="Policies">
              <Field label="Cancellation" value={data.policies.cancellation} onChange={(v) => upd("policies.cancellation", v)} />
              <Field label="Payment methods" value={data.policies.payment_methods} onChange={(v) => upd("policies.payment_methods", v)} />
            </Section>
          </div>
        )}

        {phase === "review" && (
          <div className="border-t border-slate-100 px-6 py-4 flex justify-between">
            <Button variant="ghost" onClick={() => setPhase("url")}>Start over</Button>
            <Button data-testid="import-save-btn" onClick={apply} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save &amp; continue</Button>
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, action, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2"><h4 className="font-semibold text-slate-900 text-sm">{title}</h4>{action}</div>
    <div className="space-y-2">{children}</div>
  </div>
);
const Field = ({ label, value, onChange }) => (
  <div>
    <Label className="text-xs text-slate-500 flex items-center gap-1">{label}{!value && <AlertTriangle className="h-3 w-3 text-amber-400" title="Not detected — please review" />}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`h-8 text-sm mt-0.5 ${!value ? "border-amber-200 bg-amber-50/40" : ""}`} />
  </div>
);
const PriceField = ({ label, value, onChange }) => (
  <div><Label className="text-[10px] text-slate-400">{label}</Label><Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`h-8 text-sm mt-0.5 ${!value ? "border-amber-200 bg-amber-50/40" : ""}`} /></div>
);
const TextField = ({ label, value, onChange }) => (
  <div><Label className="text-xs text-slate-500">{label}</Label><Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="text-sm mt-0.5" /></div>
);
